import { Provider } from 'store/api/enums';
import type { DataJournal } from 'store/vat/models/vatReportData';
import { SELECT_TRANSACTION_URL_PARAM } from 'utils/entityTransactionsRequestFilters';

function buildEntityTransactionsTabLink(
  organisationId: string,
  selectTransactionId?: string
): string {
  const params = new URLSearchParams({ tab: 'transactions' });
  const txId = selectTransactionId?.trim();
  if (txId) {
    params.set(SELECT_TRANSACTION_URL_PARAM, txId);
  }
  return `/entities/organisation/${organisationId}?${params.toString()}`;
}

export const getProviderLink = (
  provider?: string,
  code?: string,
  organisationId?: string,
  /** Excel / Receipts: deep-link to one transaction on the entity Transactions tab. */
  selectTransactionId?: string
) => {
  switch (provider) {
    case Provider.XERO:
      return `https://go.xero.com/app/${code}/dashboard`;
    case Provider.CIN7:
      return `https://inventory.dearsystems.com`;
    case Provider.QUICKBOOKS:
      return `https://qbo.intuit.com`;
    case Provider.ZOHO:
      return `https://books.zoho.eu/app/${code}#/`;
    case Provider.EXCEL:
    case Provider.RECEIPTS:
      return organisationId
        ? buildEntityTransactionsTabLink(organisationId, selectTransactionId)
        : null;
    default:
      return null;
  }
};

/**
 * Resolves the provider icon link for a VAT report transaction/exception row.
 * Excel and Receipts use in-app Transactions tab deep links; other providers use API sourceUrl when set.
 */
export const getVatReportTransactionProviderLink = (
  record: Pick<
    DataJournal,
    'sourceUrl' | 'uuid' | 'applicationConnectionId' | 'entityProviderName'
  >,
  providerName: string,
  organisationId?: string,
  shortCode?: string
): string | undefined => {
  const provider = (record.entityProviderName || providerName || '')
    .toLowerCase()
    .trim();
  if (!provider) return undefined;

  const orgId =
    record.applicationConnectionId?.trim() || organisationId?.trim();
  const txId = record.uuid?.trim();

  if (provider === Provider.EXCEL || provider === Provider.RECEIPTS) {
    if (!orgId) return undefined;
    return getProviderLink(provider, undefined, orgId, txId) ?? undefined;
  }

  const fromApi = record.sourceUrl?.trim();
  if (fromApi) return fromApi;

  if (!orgId && !shortCode) return undefined;
  return getProviderLink(provider, shortCode, orgId) ?? undefined;
};

export const getVIESProviderLink = (
  provider?: string,
  shortCode?: string,
  contactType?: string,
  providerContactId?: string
) => {
  switch (provider) {
    case Provider.XERO:
      return `https://go.xero.com/app/${shortCode}/contacts/contact/${providerContactId}`;
    case Provider.CIN7:
      return `https://inventory.dearsystems.com/${contactType}#${providerContactId}`;
    case Provider.QUICKBOOKS:
      return `https://qbo.intuit.com/app/customerdetail?nameId=${providerContactId}`;
    case Provider.ZOHO: {
      const linkType = contactType === 'customer' ? 'contacts' : 'vendors';
      return `https://books.zoho.eu/app/${shortCode}#/${linkType}/${providerContactId}`;
    }
  }
};

/** External accounting URL (e.g. Xero) or in-app entity Transactions tab for Excel / Receipts. */
export type ResolvedProviderLogoLink =
  | { kind: 'external'; href: string }
  | { kind: 'internal'; to: string };

/**
 * Resolves where the provider logo should navigate: external provider app when available,
 * otherwise generate.TAX entity Transactions tab for Excel and Receipts (same UX as opening Xero).
 */
export const resolveProviderLogoLink = (
  providerName: string | undefined,
  shortCode: string | undefined,
  organisationId: string | undefined
): ResolvedProviderLogoLink | null => {
  if (!providerName || !organisationId) return null;
  const p = providerName.toLowerCase();

  // Excel / Receipts navigate in-app (not an external accounting URL).
  if (p === Provider.EXCEL || p === Provider.RECEIPTS) {
    return {
      kind: 'internal',
      to: buildEntityTransactionsTabLink(organisationId),
    };
  }

  const ext = getProviderLink(p, shortCode, organisationId);
  if (!ext) return null;
  if (ext.startsWith('http://') || ext.startsWith('https://')) {
    return { kind: 'external', href: ext };
  }
  return { kind: 'internal', to: ext };
};
